import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useWorkspaceContext } from 'contexts/workspace-context';
import { getOrderTabPathname } from 'hooks/useOrderTabs/order-tab-pathname';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme: any) => ({
  tabs: {
    width: '100%',
    [theme.breakpoints.down('md')]: {
      height: theme.spacing(5),
      minHeight: theme.spacing(5),
    },
    '& .MuiTabs-scroller': {
      overflowX: 'auto !important',
      scrollbarWidth: 'thin',
    },
    '& .MuiTabScrollButton-root': {
      width: theme.spacing(4),
      flexShrink: 0,
      [theme.breakpoints.down('md')]: {
        width: theme.spacing(3.5),
      },
    },
  },
  tabIndicator: { backgroundColor: theme.palette.secondary.dark },
  tab: {
    height: theme.spacing(6),
    minHeight: theme.spacing(6),
    padding: theme.spacing(0.5),
    minWidth: theme.spacing(18),
    flexShrink: 0,
    textTransform: 'capitalize',
    alignItems: 'flex-start',
    justifyContent: 'center',
    textAlign: 'left',
    '& .MuiTab-wrapper': {
      width: '100%',
      height: '100%',
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.spacing(10),
      height: theme.spacing(5),
      minHeight: theme.spacing(5),
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  tabSelected: {
    backgroundColor: theme.palette.background.paper,
  },
  tabLabel: {
    padding: 0,
    margin: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  label: {
    marginRight: theme.spacing(1),
    fontWeight: 'bold',
    textTransform: "none",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: theme.spacing(19),
    whiteSpace: "nowrap",
    textAlign: "left"
  },
  tabOrderId: {
    fontFamily: 'monospace',
    fontVariantNumeric: 'tabular-nums',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  tabOrderIdMobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  tabOrderIdDesktop: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  tabOrderIdSelected: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: 1.25,
    cursor: 'text',
    userSelect: 'text',
  },
  labelSelected: { color: theme.palette.common.black },
  iconContainer: {
    fontSize: 14,
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    padding: 0,
  },
  iconButton: {
    fontSize: 14,
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderRadius: theme.spacing(0.5),
    zIndex: 100
  },
  icon: { color: theme.palette.dualBackground.default },
  iconSelected: { color: theme.palette.common.black },
  link: {
    textDecoration: "none", color: "inherit"
  }
}));

const OrderOpenTabs = () => {
  const classes = useStyles();

  const {
    removeOrderTab,
    selectOrderTab,
    selectedOrderIdTab,
    openedOrderIdList,
  } = useWorkspaceContext();

  const location = useLocation();

  return (
    selectedOrderIdTab ? (
      <Tabs
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons
        value={selectedOrderIdTab.id}
        TabIndicatorProps={{ className: classes.tabIndicator }}
      >
        {openedOrderIdList.map((tab) => {
          const mobileOrderId = tab.orderId.split('/').pop()?.replace(/^0+/, '') || '0';
          const tabDetails = (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Typography
                variant={tab.id === selectedOrderIdTab.id ? 'body2' : 'caption'}
                className={clsx(classes.tabOrderId, classes.tabOrderIdDesktop, {
                  [classes.tabOrderIdSelected]: tab.id === selectedOrderIdTab.id,
                })}
              >
                {tab.orderId}
              </Typography>
              <Typography
                variant={tab.id === selectedOrderIdTab.id ? 'body2' : 'caption'}
                className={clsx(classes.tabOrderId, classes.tabOrderIdMobile, {
                  [classes.tabOrderIdSelected]: tab.id === selectedOrderIdTab.id,
                })}
              >
                {mobileOrderId}
              </Typography>
              <Typography
                variant="caption"
                className={clsx(classes.label, { [classes.labelSelected]: tab.id === selectedOrderIdTab.id })}
              >
                {tab.firstPropertyAddress}
              </Typography>
            </div>
          );

          return (
            <Tab
              key={tab.id}
              value={tab.id}
              className={clsx(classes.tab, { [classes.tabSelected]: tab.id === selectedOrderIdTab.id })}
              onClick={() => selectOrderTab(tab)}
              label={(
                <div className={classes.tabLabel}>
                  {tab.id === selectedOrderIdTab.id ? tabDetails : (
                    <Link to={getOrderTabPathname(location.pathname, selectedOrderIdTab.id, tab.id)} className={classes.link}>
                      {tabDetails}
                    </Link>
                  )}

                  {selectedOrderIdTab.id !== tab.id && (
                  <div className={classes.iconContainer}>
                    <span>
                      <IconButton
                        component="div"
                        className={classes.iconButton}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          removeOrderTab(tab.id);
                        }}
                        size="large"
                      >
                        <FontAwesomeIcon
                          icon="times"
                          size="xs"
                          className={clsx(classes.icon, { [classes.iconSelected]: tab.id === selectedOrderIdTab.id })}
                        />
                      </IconButton>
                    </span>
                  </div>
                  )}
                </div>
            )}
            />
          );
        })}
      </Tabs>
    ) : null);
};

export default OrderOpenTabs;
